import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "分类", to: "/category/game", hasDropdown: true },
  { label: "搜索", to: "/search" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setMenuOpen(false);
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to.split("/").slice(0, 2).join("/"));
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-800 bg-background-950/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-950">
            <i className="ri-live-line text-lg" />
          </span>
          <span className="font-heading text-lg font-extrabold tracking-tight text-foreground-50">
            主播<span className="text-primary-500">视频</span>
          </span>
        </Link>

        <nav className="ml-2 hidden items-center gap-1 md:flex">
          {navLinks.map((link) =>
            link.hasDropdown ? (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                <Link
                  to={link.to}
                  className={`flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive(link.to)
                      ? "text-primary-400"
                      : "text-foreground-300 hover:text-foreground-50"
                  }`}
                >
                  {link.label}
                  <i className={`ri-arrow-down-s-line text-base transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                </Link>
                {dropdownOpen && (
                  <div className="absolute left-0 top-full w-52 pt-2">
                    <div className="rounded-lg border border-background-800 bg-background-900 p-1.5">
                      {categories.map((cat) => (
                        <Link
                          key={cat.key}
                          to={`/category/${cat.key}`}
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground-300 transition-colors hover:bg-background-800 hover:text-foreground-50"
                        >
                          <i className={`${cat.icon} text-base text-primary-500`} />
                          <span>{cat.name}视频</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.label}
                to={link.to}
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? "text-primary-400"
                    : "text-foreground-300 hover:text-foreground-50"
                }`}
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden items-center md:flex">
          <div className="flex items-center gap-2 rounded-md border border-background-700 bg-background-900 px-3 py-1.5 focus-within:border-primary-500">
            <i className="ri-search-line text-sm text-foreground-500" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索视频、主播"
              className="w-40 bg-transparent text-sm text-foreground-100 outline-none placeholder:text-foreground-600 lg:w-56"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded bg-primary-500 px-2.5 py-1 font-label text-xs font-semibold text-background-950 transition-colors hover:bg-primary-400"
            >
              搜索
            </button>
          </div>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-700 text-foreground-100 md:hidden"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-800 bg-background-950 px-4 pb-5 pt-4 md:hidden">
          <form onSubmit={submitSearch} className="mb-4">
            <div className="flex items-center gap-2 rounded-md border border-background-700 bg-background-900 px-3 py-2 focus-within:border-primary-500">
              <i className="ri-search-line text-sm text-foreground-500" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索视频、主播"
                className="w-full bg-transparent text-sm text-foreground-100 outline-none placeholder:text-foreground-600"
              />
            </div>
          </form>
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                onClick={() => setMenuOpen(false)}
                className={`rounded-md px-3 py-2.5 text-sm font-medium ${
                  isActive(link.to)
                    ? "bg-background-900 text-primary-400"
                    : "text-foreground-300"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="mt-3 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.key}
                to={`/category/${cat.key}`}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-1.5 rounded-full border border-background-700 px-3 py-1.5 text-xs text-foreground-300"
              >
                <i className={`${cat.icon} text-primary-500`} />
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}